import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addRelease } from '../../services/releaseService';
import { isAuthenticated } from '../../services/artistService';
import { compressImage } from '../../services/imageService';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { ChevronLeft, Save, Loader2, Trash2 } from 'lucide-react';
import { Release } from '../../types';

export const AddRelease: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<Omit<Release, 'id'>>({
        title: '',
        artist: '',
        type: 'Single',
        date: '',
        cover: '',
        catalogNumber: '',
        spotifyId: '',
        spotifyUrl: '',
        spotifyEmbedType: 'track',
        soundcloudUrl: ''
    });

    const [isCompressing, setIsCompressing] = useState(false);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/admin/login');
        }
    }, [navigate]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsCompressing(true);
        try {
            // Compress image to under 1MB
            const compressedImage = await compressImage(file, 1);
            setFormData(prev => ({ ...prev, cover: compressedImage }));
        } catch (error) {
            console.error('Error compressing image:', error);
            alert('Failed to process image. Please try another file.');
        } finally {
            setIsCompressing(false);
        }
    };

    const fetchMetadata = async () => {
        if (!formData.spotifyUrl) {
            alert('Please enter a Spotify URL first.');
            return;
        }

        try {
            const response = await fetch(`/api/spotify-metadata?url=${encodeURIComponent(formData.spotifyUrl)}`);
            if (!response.ok) {
                throw new Error('Failed to fetch metadata');
            }
            const data = await response.json();
            
            // Data from our scraping API: { title, artist, releaseDate, releaseType, image }
            
            setFormData(prev => ({
                ...prev,
                title: data.title || prev.title,
                artist: data.artist || prev.artist,
                cover: data.image || prev.cover,
                date: data.releaseDate || prev.date,
                type: (data.releaseType === 'Album' || data.releaseType === 'EP' || data.releaseType === 'Single') ? data.releaseType : 'Single',
                // Extract Spotify ID from URL if possible
                spotifyId: extractSpotifyId(formData.spotifyUrl || '') || prev.spotifyId,
                spotifyEmbedType: (formData.spotifyUrl || '').includes('album') ? 'album' : 'track'
            }));

            if (!data.image) {
                alert('Could not retrieve cover image from Spotify. Please upload manually.');
            }

        } catch (error) {
            console.error('Error fetching metadata:', error);
            alert('Could not fetch metadata. Please fill manually.');
        }
    };

    const extractSpotifyId = (url: string) => {
        const match = url.match(/(track|album|artist)\/([a-zA-Z0-9]+)/);
        return match ? match[2] : '';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Basic validation
        if (!formData.title || !formData.artist || !formData.cover || !formData.date) {
            alert('Please fill in all required fields (Title, Artist, Cover URL, Date)');
            return;
        }

        try {
            await addRelease(formData);
            navigate('/admin/dashboard');
        } catch (error) {
            alert('Failed to add release. Please try again.');
        }
    };

    return (
        <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <button
                    onClick={() => navigate('/admin/dashboard')}
                    className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
                >
                    <ChevronLeft size={16} /> Back to Dashboard
                </button>

                <div className="bg-black/50 border border-white/10 rounded-xl p-8 backdrop-blur-md">
                    <SectionHeader title="Add New Release" subtitle="Expand the Archive" />

                    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Title */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Release Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="e.g. Cosmic Dust"
                                />
                            </div>

                            {/* Artist */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Artist Name *</label>
                                <input
                                    type="text"
                                    name="artist"
                                    value={formData.artist}
                                    onChange={handleChange}
                                    required
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="e.g. FL3X"
                                />
                            </div>

                            {/* Type */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Release Type *</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    required
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                >
                                    <option value="Single">Single</option>
                                    <option value="EP">EP</option>
                                    <option value="Album">Album</option>
                                </select>
                            </div>

                            {/* Date */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Release Date *</label>
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    required
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                />
                            </div>

                            {/* Catalog Number */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Catalog Number</label>
                                <input
                                    type="text"
                                    name="catalogNumber"
                                    value={formData.catalogNumber}
                                    onChange={handleChange}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="e.g. AV005"
                                />
                            </div>

                            {/* Cover Image */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Cover Image *</label>
                                <div className="flex flex-col gap-4">
                                    {/* Preview */}
                                    {formData.cover && (
                                        <div className="relative w-48 h-48 rounded-md overflow-hidden border border-white/20">
                                            <img 
                                                src={formData.cover} 
                                                alt="Preview" 
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, cover: '' }))}
                                                className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-red-500 transition-colors"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                                        {/* Upload Button */}
                                        <label className={`flex items-center justify-center px-4 py-2 bg-white/10 text-white rounded-md cursor-pointer hover:bg-white/20 transition-colors border border-white/20 ${isCompressing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                            <span className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                                                {isCompressing ? <Loader2 className="animate-spin w-4 h-4" /> : 'Upload File'}
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                className="hidden"
                                                disabled={isCompressing}
                                            />
                                        </label>
                                        <span className="text-sm text-gray-400">OR</span>
                                        {/* URL Input */}
                                        <input
                                            type="url"
                                            name="cover"
                                            value={formData.cover}
                                            onChange={handleChange}
                                            className="flex-grow bg-black/80 border border-white/20 rounded-md p-2 text-white focus:border-astral-purple focus:outline-none text-sm"
                                            placeholder="https://example.com/cover.jpg"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500">Auto-filled from Spotify or upload manually (max 5MB, auto-compressed).</p>
                                </div>
                            </div>

                            {/* Spotify ID */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Spotify ID</label>
                                <input
                                    type="text"
                                    name="spotifyId"
                                    value={formData.spotifyId}
                                    onChange={handleChange}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="e.g. 0vYuIMNOg2Z02uXDqMKlUk"
                                />
                            </div>

                            {/* Spotify Embed Type */}
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Spotify Embed Type</label>
                                <select
                                    name="spotifyEmbedType"
                                    value={formData.spotifyEmbedType}
                                    onChange={handleChange}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                >
                                    <option value="track">Track</option>
                                    <option value="album">Album</option>
                                </select>
                            </div>

                            {/* Spotify URL */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Spotify URL</label>
                                <div className="flex gap-2">
                                    <input
                                        type="url"
                                        name="spotifyUrl"
                                        value={formData.spotifyUrl}
                                        onChange={handleChange}
                                        className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                        placeholder="https://open.spotify.com/album/..."
                                    />
                                    <button
                                        type="button"
                                        onClick={fetchMetadata}
                                        className="px-4 py-2 bg-white/10 text-white rounded-md hover:bg-white/20 transition-colors whitespace-nowrap text-sm font-bold uppercase tracking-wider"
                                    >
                                        Auto-Fill
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Paste Spotify URL and click Auto-Fill to fetch cover and details.</p>
                            </div>

                            {/* SoundCloud URL */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-300 mb-1">SoundCloud URL</label>
                                <input
                                    type="url"
                                    name="soundcloudUrl"
                                    value={formData.soundcloudUrl || ''}
                                    onChange={handleChange}
                                    className="w-full bg-black/80 border border-white/20 rounded-md p-3 text-white focus:border-astral-purple focus:outline-none"
                                    placeholder="https://soundcloud.com/astralvibe/..."
                                />
                            </div>
                        </div>

                        <div className="pt-6 border-t border-white/10 flex justify-end">
                            <button
                                type="submit"
                                className="flex items-center gap-2 px-8 py-3 bg-astral-purple text-white font-bold uppercase tracking-wider rounded-md hover:bg-astral-purple/80 transition-all shadow-lg hover:shadow-astral-purple/30"
                            >
                                <Save size={18} /> Save Release
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
